/**
 * Feature Announcements Service
 * Manages feature log entries and user dismissals
 */

import { supabase } from '@/lib/supabase';

export interface FeatureAnnouncement {
  id: string;
  title: string;
  description: string;
  detailed_description: string | null;
  category: 'feature' | 'improvement' | 'fix';
  icon: string;
  color: string;
  route: string | null;
  route_label: string | null;
  image_url: string | null;
  version: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  created_by_name: string | null;
}

export interface FeatureAnnouncementInsert {
  title: string;
  description: string;
  detailed_description?: string | null;
  category: 'feature' | 'improvement' | 'fix';
  icon?: string;
  color?: string;
  route?: string | null;
  route_label?: string | null;
  image_url?: string | null;
  version?: string | null;
  is_active?: boolean;
}

export interface FeatureAnnouncementUpdate {
  title?: string;
  description?: string;
  detailed_description?: string | null;
  category?: 'feature' | 'improvement' | 'fix';
  icon?: string;
  color?: string;
  route?: string | null;
  route_label?: string | null;
  image_url?: string | null;
  version?: string | null;
  is_active?: boolean;
}

/**
 * Get all active feature announcements, ordered newest first
 */
export async function getFeatureAnnouncements(): Promise<FeatureAnnouncement[]> {
  const { data, error } = await supabase
    .from('feature_announcements')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching feature announcements:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Get feature IDs that the current user has dismissed
 */
export async function getDismissedFeatureIds(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('feature_dismissals')
    .select('feature_id')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching dismissed features:', error);
    return [];
  }

  return (data || []).map((d) => d.feature_id);
}

/**
 * Dismiss a feature announcement for a user (marks as seen)
 */
export async function dismissFeature(userId: string, featureId: string): Promise<void> {
  const { error } = await supabase
    .from('feature_dismissals')
    .upsert(
      { user_id: userId, feature_id: featureId },
      { onConflict: 'user_id,feature_id' }
    );

  if (error) {
    console.error('Error dismissing feature:', error);
    throw new Error(error.message);
  }
}

/**
 * Dismiss all features for a user
 */
export async function dismissAllFeatures(userId: string, featureIds: string[]): Promise<void> {
  const records = featureIds.map((featureId) => ({
    user_id: userId,
    feature_id: featureId,
  }));

  const { error } = await supabase
    .from('feature_dismissals')
    .upsert(records, { onConflict: 'user_id,feature_id' });

  if (error) {
    console.error('Error dismissing all features:', error);
    throw new Error(error.message);
  }
}

/**
 * Get undismissed features for a user (for notifications)
 */
export async function getUndismissedFeatures(userId: string): Promise<FeatureAnnouncement[]> {
  // Get all active features
  const features = await getFeatureAnnouncements();
  
  // Get dismissed feature IDs
  const dismissedIds = await getDismissedFeatureIds(userId);
  
  // Filter to only undismissed
  return features.filter((f) => !dismissedIds.includes(f.id));
}

// =============================================================================
// ADMIN: CRUD for feature announcements (record changes when posting to GitHub)
// =============================================================================

/**
 * Get all feature announcements including inactive (for admin list)
 */
export async function getAllFeatureAnnouncementsForAdmin(): Promise<FeatureAnnouncement[]> {
  const { data, error } = await supabase
    .from('feature_announcements')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching all feature announcements:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Create a new feature announcement
 */
export async function createFeatureAnnouncement(
  input: FeatureAnnouncementInsert,
  createdByUserId?: string,
  createdByName?: string
): Promise<FeatureAnnouncement> {
  const { data, error } = await supabase
    .from('feature_announcements')
    .insert({
      title: input.title,
      description: input.description,
      detailed_description: input.detailed_description ?? null,
      category: input.category,
      icon: input.icon ?? 'Sparkles',
      color: input.color ?? 'blue',
      route: input.route ?? null,
      route_label: input.route_label ?? null,
      image_url: input.image_url ?? null,
      version: input.version ?? null,
      is_active: input.is_active ?? true,
      created_by: createdByUserId ?? null,
      created_by_name: createdByName ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating feature announcement:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update a feature announcement
 */
export async function updateFeatureAnnouncement(
  id: string,
  updates: FeatureAnnouncementUpdate
): Promise<FeatureAnnouncement> {
  const { data, error } = await supabase
    .from('feature_announcements')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating feature announcement:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Set announcement active or inactive (soft delete)
 */
export async function setFeatureAnnouncementActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('feature_announcements')
    .update({ is_active: isActive })
    .eq('id', id);

  if (error) {
    console.error('Error updating feature announcement active state:', error);
    throw new Error(error.message);
  }
}
